import {Injectable} from '@angular/core';
import {Observable, tap} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {ApiUtil} from '@shared/util/apiUtil';
import {CreatePlaceRequest, PlaceDTO, PlaceRank, PlaceStats} from '@domain/place/place.dto';
import {NotificationService} from "@shared/notification/notification.service";

@Injectable({
  providedIn: 'root'
})
export class PlaceApi {

  constructor(
    private readonly http: HttpClient,
    private readonly notificationService: NotificationService
  ) { }

  public getRegions(): Observable<PlaceDTO[]> {
    return this.http.get<PlaceDTO[]>(ApiUtil.buildUrl(this.getPath(), '/region'));
  }

  public getRegionByCode(regionCode: string): Observable<PlaceDTO | null> {
    return this.http.get<PlaceDTO | null>(ApiUtil.buildUrl(this.getPath(), `/region/${regionCode}`));
  }

  public search(term: string, rank?: PlaceRank | null): Observable<PlaceDTO[]> {
    const queryParams: {[key: string]: string;} = {term: term};
    if (rank) {
      queryParams['rank'] = rank;
    }
    return this.http.get<PlaceDTO[]>(ApiUtil.buildUrl(this.getPath(), '/search', queryParams));
  }

  public getById(placeId: string): Observable<PlaceDTO | null> {
    return this.http.get<PlaceDTO | null>(ApiUtil.buildUrl(this.getPath(placeId)));
  }

  public create(createRequest: CreatePlaceRequest): Observable<PlaceDTO> {
    return this.http.post<PlaceDTO>(ApiUtil.buildUrl(this.getPath()), createRequest);
  }

  public update(placeId: string, updateRequest: Partial<CreatePlaceRequest>): Observable<PlaceDTO> {
    return this.http.patch<PlaceDTO>(ApiUtil.buildUrl(this.getPath(placeId)), updateRequest);
  }

  public delete(placeId: string): Observable<void> {
    return this.http.delete<void>(ApiUtil.buildUrl(this.getPath(placeId))).pipe(
      tap(() => {
        this.notificationService.toast({
          message: 'Place has been deleted.'
        });
      })
    );
  }

  public getStats(placeId: string): Observable<PlaceStats | null> {
    return this.http.get<PlaceStats | null>(ApiUtil.buildUrl(this.getPath(placeId), '/stats'));
  }

  public exportAll(): Observable<Blob> {
    return this.http.get(ApiUtil.buildUrl(this.getPath(), '/export'), {responseType: 'blob'});
  }

  public importAll(file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(), '/import'), formData);
  }

  private getPath(placeId?: string): string {
    const prefix = 'place';
    return placeId
      ? `${prefix}/${placeId}`
      : prefix;
  }
}
